import CashPayment from 'app/src/payments/CashPayment';
import UserDefinedPayment from 'app/src/payments/UserDefinedPayment';
import GiftCardPayment from 'app/src/payments/GiftCardPayment';
import SwipePayment from 'app/src/payments/SwipePayment';
import TerminalPayment from 'app/src/payments/TerminalPayment';
import CreditAccountPayment from 'app/src/payments/CreditAccountPayment';
import PendingPayment from 'app/src/payments/PendingPayment';
import DepositPayment from 'app/src/payments/DepositPayment';
import EcomPayment from 'app/src/payments/EcomPayment';
import AdjustmentPayment from 'app/src/payments/AdjustmentPayment';
import PaymentType from 'app/src/data/models/PaymentType';
import CCCharge from 'app/src/data/models/CCCharge';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';

/**
 * Class definition for the PaymentFactory.  Constructs concrete instances of the payments
 * that are used to perform payments against the SaleManager.
 */
export default class PaymentFactory {
  /**
   * Creates a concrete payment type.
   *
   * @param paymentType {String}          The type of payment to construct
   * @param paymentData {*}               Any arguments that need to be passed to construct
   *                                      the payment type.
   *
   * @returns {*|null}                    The concrete payment or null if the payment type is
   *                                      not recognized.
   */
  static createPayment(paymentType, ...paymentData) {
    let payment = null;

    switch (paymentType) {
      case PaymentTypeManager.Types.CASH:
        payment = new CashPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.USER_DEFINED:
        payment = new UserDefinedPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.GIFT_CARD:
        payment = new GiftCardPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.SWIPE:
        payment = new SwipePayment(...paymentData);
        break;

      case PaymentTypeManager.Types.TERMINAL:
        payment = new TerminalPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.CREDIT_ACCOUNT:
        payment = new CreditAccountPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.PENDING:
        payment = new PendingPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.DEPOSIT:
        payment = new DepositPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.ECOM:
        payment = new EcomPayment(...paymentData);
        break;

      case PaymentTypeManager.Types.ADJUSTMENT:
        payment = new AdjustmentPayment(...paymentData);
        break;

      default:
        console.error('Unknown payment type', paymentType);
        break;
    }

    return payment;
  }

  /**
   * Create a concrete payment type from a SalePayment model.
   *
   * @param salePayment {SalePayment}     The SalePayment model used to construct the concrete
   *                                      Payment object.
   * @returns {*|null}                    The concrete payment or null if the factory cannot
   *                                      determine the type of payment.
   */
  static createPaymentFromModel(salePayment, paymentServiceModel = null) {
    let payment = null;

    let paymentType = salePayment.getRelation('PaymentType', PaymentType);
    if (paymentType) {
      switch (paymentType.get('type')) {
        case PaymentType.Types.GIFT_CARD:
          payment = PaymentFactory.createPayment(PaymentTypeManager.Types.GIFT_CARD);
          break;

        case PaymentType.Types.CASH:
          payment = PaymentFactory.createPayment(PaymentTypeManager.Types.CASH);
          break;

        case PaymentType.Types.CREDIT_ACCOUNT:
          payment = PaymentFactory.createPayment(PaymentTypeManager.Types.CREDIT_ACCOUNT);
          break;

        case PaymentType.Types.CREDIT_CARD:
          if (salePayment.has('paymentID') && salePayment.get('paymentID').length) {
            // payment is using the payment service so this must be a terminal payment
            payment = PaymentFactory.createPayment(PaymentTypeManager.Types.TERMINAL);
          } else {
            let ccCharge = salePayment.getRelation('CCCharge', CCCharge);

            if (ccCharge) {
              // assume that if the CC Charge has swipe data then it's a card reader
              // transaction (i.e. non-terminal)
              if (ccCharge.isSwipe()) {
                payment = PaymentFactory.createPayment(PaymentTypeManager.Types.SWIPE);
              } else {
                payment = PaymentFactory.createPayment(PaymentTypeManager.Types.TERMINAL);
              }
            } else {
              // If there's no ccCharge then let's just assume it's some sort of wonky
              // terminal payment
              payment = PaymentFactory.createPayment(PaymentTypeManager.Types.TERMINAL);
            }
          }
          break;

        case PaymentType.Types.USER_DEFINED:
          payment = PaymentFactory.createPayment(PaymentTypeManager.Types.USER_DEFINED);
          break;

        case PaymentType.Types.ECOM:
          payment = PaymentFactory.createPayment(PaymentTypeManager.Types.ECOM, paymentType.id);
          break;

        case PaymentType.Types.ADJUSTMENT:
          payment = PaymentFactory.createPayment(
            PaymentTypeManager.Types.ADJUSTMENT,
            paymentType.id
          );
          break;

        default:
          break;
      }

      if (payment) {
        payment.model = salePayment;
        if (paymentServiceModel) {
          payment.serviceModel = paymentServiceModel;
        }
      }
    }

    if (!payment) {
      console.error('Unable to determine the payment type of:', salePayment);
    }

    return payment;
  }

  static createPaymentsFromModels(salePayments, servicePayments) {
    let payments = [];

    return payments;
  }
}
